import React, { memo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { makeStyles } from '@mui/styles';

import CheckmarkIcon from '../icons/Checkmark';
import OutlinedSelectIcon from '../icons/OutlinedSelect';
import NestedMenuItem from './NestedMenuItem';
import MenuItem from './MenuItem';
import palette from '../styles/palette';

const useStyles = makeStyles(() => ({
  input: {
    caretColor: 'transparent',
    cursor: 'pointer',
    '& *': {
      cursor: 'pointer',
    },

    '&:hover': {
      cursor: 'pointer',
    },
  },
  icon: {
    transition: 'transform linear 200ms',
    transform: 'rotate(-180deg)',
  },
  iconOpen: {
    color: palette.blue,
    transform: 'rotate(0deg)',
  },
}));

const findOption = (options, value) => {
  for (const option of options) {
    if (option.value instanceof Array) {
      const result = findOption(option.value, value);

      if (result) {
        return result;
      }
    }

    if (option.value === value) {
      return option;
    }
  }
};

const NestedSelect = ({
  onChange,
  value,
  options,
  label,
  maxHeight = 300,
  renderInputLabel = (option, label, open) =>
    option?.label || (!open ? label : ''),
  helperText,
  placeholder,
  disabled,
  ...props
}) => {
  const [activeOption, setActiveOption] = useState(undefined);
  const [displayNode, setDisplayNode] = useState(undefined);
  const [open, setOpen] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    setActiveOption(findOption(options, value));
  }, [options, value]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = useCallback(() => {
    if (disabled) {
      return;
    }

    setOpen(true);
  }, [disabled]);

  const handleSelectMenuItem = useCallback(
    (e, value) => {
      handleClose();
      onChange(value);
    },
    [onChange]
  );

  const renderItem = useCallback(
    (item, index) => {
      if (item.value instanceof Array) {
        return (
          <NestedMenuItem
            key={index}
            parentMenuOpen={open}
            label={item.label}
            maxHeight={maxHeight}
          >
            {item.value.map(renderItem)}
          </NestedMenuItem>
        );
      }

      return (
        <MenuItem
          value={item.value}
          key={item.value}
          onClick={handleSelectMenuItem}
          rightIcon={item.value === value ? <CheckmarkIcon /> : null}
        >
          {item.label}
        </MenuItem>
      );
    },
    [handleSelectMenuItem, maxHeight, open, value]
  );

  return (
    <>
      <TextField
        variant="outlined"
        fullWidth
        InputLabelProps={{ shrink: open || activeOption }}
        {...props}
        className={classes.input}
        inputProps={{
          className: classes.input,
        }}
        value={renderInputLabel(activeOption, label, open)}
        readOnly
        placeholder={placeholder}
        label={(activeOption || open) && label}
        focused={open}
        helperText={helperText}
        onClick={handleOpen}
        inputRef={setDisplayNode}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <OutlinedSelectIcon
                className={clsx(classes.icon, {
                  [classes.iconOpen]: open,
                })}
              />
            </InputAdornment>
          ),
        }}
      />
      <Popover
        anchorEl={displayNode}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        style={{
          maxHeight,
        }}
        open={open}
        onClose={handleClose}
      >
        {options.map(renderItem)}
      </Popover>
    </>
  );
};

NestedSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.string,
        PropTypes.number,
      ]),
      label: PropTypes.node,
    })
  ).isRequired,
  maxHeight: PropTypes.number,
  renderInputLabel: PropTypes.func,
  helperText: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};

export default memo(NestedSelect);
